import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utcPlugin from "dayjs/plugin/utc";
import { Controller, useForm } from "react-hook-form";
import { ActivityType } from "generated/graphql";
import { useLogActivity } from "hooks/activities/useLogActivity";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const ACTIVITY_LABEL_MAP: Record<string, string> = {
  CALL: "Call",
  TEXT: "Text Message",
  EMAIL: "Email",
  MEETING: "Meeting",
  NOTE: "Note",
};

const LOGGABLE_ACTIVITY_TYPES: ActivityType[] = [
  ActivityType.CALL,
  ActivityType.TEXT,
  ActivityType.EMAIL,
  ActivityType.MEETING,
  ActivityType.NOTE,
];

type LogActivityFormValues = {
  type: ActivityType;
  summary: string;
  body: string;
  occurredAtLocal: string;
};

type LogActivityModalProps = {
  open: boolean;
  onClose: () => void;
  contactId?: string | null;
  companyId?: string | null;
  opportunityId?: string | null;
  proposalId?: string | null;
  timezone: string;
  requiresOpportunity?: boolean;
  requiresProposal?: boolean;
};

const defaultFormValues = (): LogActivityFormValues => ({
  type: ActivityType.CALL,
  summary: "",
  body: "",
  occurredAtLocal: dayjs().format("YYYY-MM-DDTHH:mm"),
});

const labelForType = (type: string) => {
  const normalizedType = (type ?? "").toUpperCase();
  return ACTIVITY_LABEL_MAP[normalizedType] ?? "Activity";
};

export default function LogActivityModal({
  open,
  onClose,
  contactId,
  companyId,
  opportunityId,
  proposalId,
  timezone,
  requiresOpportunity = false,
  requiresProposal = false,
}: LogActivityModalProps) {
  const {
    handleSubmit,
    control,
    register,
    reset,
    watch,
    formState: { isValid },
  } = useForm<LogActivityFormValues>({
    defaultValues: defaultFormValues(),
    mode: "onChange",
  });

  const summaryValue = watch("summary");
  const occurredAtLocal = watch("occurredAtLocal");

  const shouldRefetchActivities = !!contactId && (!requiresOpportunity || !!opportunityId);

  const [logActivity, logActivityResult] = useLogActivity({
    contactId,
    opportunityId,
    shouldRefetch: shouldRefetchActivities,
  });

  const hasRequiredIds =
    !!contactId && (!requiresOpportunity || !!opportunityId) && (!requiresProposal || !!proposalId);
  const canLog = hasRequiredIds && !!summaryValue.trim() && !!occurredAtLocal && isValid;

  const warningMessage = requiresProposal
    ? "Select a contact and load the proposal first."
    : requiresOpportunity
      ? "Select an opportunity (with a contact) first."
      : "Select a contact first.";

  const onSubmit = async (values: LogActivityFormValues) => {
    if (!hasRequiredIds) return;

    const occurredAtUtc = dayjs.tz(values.occurredAtLocal, timezone).utc().format("YYYY-MM-DD HH:mm:ss");

    await logActivity({
      variables: {
        input: {
          contactId: contactId as string,
          companyId: companyId ?? null,
          opportunityId: opportunityId ?? null,
          proposalId: proposalId ?? null,
          type: values.type,
          summary: values.summary.trim(),
          body: values.body.trim() ? values.body.trim() : null,
          occurredAt: occurredAtUtc,
        },
      },
    });

    onClose();
    reset(defaultFormValues());
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Log Activity</DialogTitle>
      <DialogContent>
        {!hasRequiredIds ? <MDAlert color="warning">{warningMessage}</MDAlert> : null}

        {logActivityResult.error ? (
          <MDBox mt={1}>
            <MDAlert color="error">{logActivityResult.error.message}</MDAlert>
          </MDBox>
        ) : null}

        <MDBox mt={2} display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth size="medium" sx={{ '& .MuiSelect-select': { "line-height": "2.25em" } }}>
            <InputLabel id="activity-type-label">Type</InputLabel>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select {...field} labelId="activity-type-label" label="Type" size="medium">
                  {LOGGABLE_ACTIVITY_TYPES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {labelForType(value)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>

          <TextField label="Summary" {...register("summary", { required: true })} fullWidth required />

          <TextField
            label="Details (optional)"
            {...register("body")}
            fullWidth
            multiline
            minRows={3}
          />

          <TextField
            label="When did it happen?"
            type="datetime-local"
            {...register("occurredAtLocal", { required: true })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={logActivityResult.loading}>
          Cancel
        </MDButton>

        <MDButton
          variant="gradient"
          color="success"
          onClick={handleSubmit(onSubmit)}
          disabled={!canLog || logActivityResult.loading}
        >
          Save
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
